import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import { Pill, Map as MapIcon } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useNavigation } from "../../../hooks/useNavigation";
import { resolvePharmacyId } from "../pharmacyCoverage/coverageData";
import { PharmacyCoverageNavState } from "../pharmacyCoverage/PharmacyShipToCoverage";
import {
  fetchRxRequiredOpenAccessProducts,
  fetchPracticesByUserRolePrescription,
  usePrescriptionSlice,
  RxOpenAccessProduct,
} from "../../../redux/slice/prescription/prescriptionSlice";
import { getRxAgreementPracticeLookupService } from "../../../service/PrescriptionService";
import {
  fetchPracticeProductsDataUsingDateOfServiceAndProvider,
  usePracticeProductsSlice,
} from "../../../redux/slice/practice/practiceProductSlice";
import { fetchPharmaciesList, useCommonReducer } from "../../../redux/slice/commonSlice";
import { PRODUCT_CONFIG_VALUE } from "../../../constants/applicationConstants";
import { priceFormatter, resolvePharmacyKey } from "../../../utils/Utility";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";

export type RxProductSelectionContext = {
  practiceId?: number;
};

interface RxProductSelectionPopupProps {
  open: boolean;
  onClose: () => void;
  onProductSelect: (product: RxOpenAccessProduct, context?: RxProductSelectionContext) => void;
  /** CS-2075 — Patient List → Request Rx: practice is fixed by the patient (dropdown hidden). */
  lockedPracticeId?: number;
}

const EXECUTED_AGREEMENT = "EXECUTED";

const isAgreementMandatoryMode = (agreementMode?: string | null) =>
  agreementMode === PRODUCT_CONFIG_VALUE.MANDATORY;

/** CS-2075 — SKU-priced agreements have no flat price; use the SKU price when every SKU shares one. */
const singleSkuExtendedPrice = (row: any): number | null => {
  const skus = row?.skuAllowablePrices ?? row?.skuExtendedPrices;
  if (!Array.isArray(skus)) return null;
  const prices = Array.from(
    new Set(
      skus
        .filter((sku: any) => sku?.extendedPrice != null && sku.extendedPrice !== "")
        .map((sku: any) => Number(sku.extendedPrice))
        .filter((price) => Number.isFinite(price))
    )
  );
  return prices.length === 1 ? prices[0] : null;
};

/** Dedupes practice-product fetch for agreement-gated Rx (survives React Strict Mode remount). */
let rxAgreementPracticeProductsFetchKey = "";

const resetRxAgreementPracticeProductsFetchKey = () => {
  rxAgreementPracticeProductsFetchKey = "";
};

const RxProductSelectionPopup: React.FC<RxProductSelectionPopupProps> = ({
  open,
  onClose,
  onProductSelect,
  lockedPracticeId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();

  const viewCoverage = (pharmacyFilter: string, productScoped = false) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/pharmacy_coverage", {
      state: { compoundType: "sterile", pharmacyFilter, productScoped } as PharmacyCoverageNavState,
    });
  };

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    cancelBtnSx,
    scrollAreaSx,
    dropdownWrapSx,
  } = useModalStyles();

  const {
    rxOpenAccessProducts,
    rxOpenAccessProductsFetchStatus,
    practicesByUserRole,
    practicesFetchStatus,
  } = usePrescriptionSlice();
  const { pharmacyInfoList } = useCommonReducer();
  const { practiceProductsInfo, practiceProductsDataFetchStatus } = usePracticeProductsSlice();

  const [searchText, setSearchText] = useState("");
  const [agreementPractices, setAgreementPractices] = useState<
    { practiceId: number; practiceName: string }[]
  >([]);
  const [agreementPracticesLoading, setAgreementPracticesLoading] = useState(false);

  /** CS-2075 — products are shown for one pharmacy at a time. */
  const { control, watch, setValue, reset } = useForm<{ pharmacyId: string; practiceId: string }>({
    defaultValues: { pharmacyId: "", practiceId: "" },
  });
  const selectedPharmacyId = watch("pharmacyId");
  // const selectedPracticeId = watch("practiceId");
  // CS-2075 — a locked practice (from the patient) wins over the dropdown.
  const watchedPracticeId = watch("practiceId");
  const selectedPracticeId = lockedPracticeId != null ? String(lockedPracticeId) : watchedPracticeId;

  useEffect(() => {
    if (open) {
      // Products are fetched once; the pharmacy dropdown only filters them.
      dispatch(fetchRxRequiredOpenAccessProducts({ skipLoader: true }));
      dispatch(fetchPharmaciesList({ skipLoader: true }));
      dispatch(fetchPracticesByUserRolePrescription({ isActivePracticeOnly: true, skipLoader: true }));
    }
  }, [open, dispatch]);

  const pharmacies = useMemo(() => pharmacyInfoList ?? [], [pharmacyInfoList]);
  const pharmacyOptions = useMemo(
    () =>
      pharmacies.map((pharmacy) => ({
        label: pharmacy.manufacturerName,
        value: pharmacy.manufacturerId,
      })),
    [pharmacies]
  );

  const selectedPharmacy = useMemo(
    () => pharmacies.find((p) => Number(p.manufacturerId) === Number(selectedPharmacyId)),
    [pharmacies, selectedPharmacyId]
  );

  const rxProductsForSelectedPharmacy = useMemo(() => {
    if (selectedPharmacyId === "") return [];
    return (rxOpenAccessProducts ?? []).filter(
      (product) => Number(product.manufacturerId) === Number(selectedPharmacyId)
    );
  }, [rxOpenAccessProducts, selectedPharmacyId]);

  /** Any Rx open-access product with AGREEMENT MODE = MANDATORY for this pharmacy. */
  const requiresMandatoryAgreementPractice = useMemo(
    () => rxProductsForSelectedPharmacy.some((product) => isAgreementMandatoryMode(product.agreementMode)),
    [rxProductsForSelectedPharmacy]
  );

  const isGenesisPharmacySelected = useMemo(
    () =>
      !!selectedPharmacy &&
      resolvePharmacyKey(selectedPharmacy.manufacturerName, selectedPharmacy.isPharmacy) === "genesis",
    [selectedPharmacy]
  );

  /** Mandatory agreement products and/or Genesis pharmacy require practice + executed agreements. */
  const needsPracticeSelection = requiresMandatoryAgreementPractice || isGenesisPharmacySelected;

  /** Alias for practice dropdown visibility (mandatory agreement or Genesis). */
  // const showPracticeDropdown = needsPracticeSelection;
  const showPracticeDropdown = needsPracticeSelection && lockedPracticeId == null; // CS-2075

  const rxOpenAccessProductIdsForPharmacy = useMemo(
    () => new Set(rxProductsForSelectedPharmacy.map((product) => Number(product.productId))),
    [rxProductsForSelectedPharmacy]
  );

  const mandatoryAgreementProductIds = useMemo(() => {
    const ids = rxProductsForSelectedPharmacy
      // .filter((product) => isAgreementMandatoryMode(product.agreementMode))
      // CS-2075 — Genesis lists only EXECUTED-agreement products, so look up practices by every Rx product of
      // the pharmacy; mandatory-only hid practices whose agreement is on a non-mandatory product.
      .filter((product) => isGenesisPharmacySelected || isAgreementMandatoryMode(product.agreementMode))
      .map((product) => Number(product.productId));
    return Array.from(new Set(ids));
  }, [rxProductsForSelectedPharmacy, isGenesisPharmacySelected]);

  const practiceOptions = useMemo(() => {
    const practices = requiresMandatoryAgreementPractice
      ? agreementPractices
      : isGenesisPharmacySelected
        ? (practicesByUserRole ?? []).map((practice) => ({
            practiceId: practice.practiceId,
            practiceName: practice.practiceName,
          }))
        : [];
    return practices.map((practice) => ({
      label: practice.practiceName,
      value: practice.practiceId,
    }));
  }, [
    agreementPractices,
    isGenesisPharmacySelected,
    practicesByUserRole,
    requiresMandatoryAgreementPractice,
  ]);

  useEffect(() => {
    if (!open || !requiresMandatoryAgreementPractice || selectedPharmacyId === "") {
      setAgreementPractices([]);
      setAgreementPracticesLoading(false);
      return;
    }
    if (mandatoryAgreementProductIds.length === 0) {
      setAgreementPractices([]);
      setAgreementPracticesLoading(false);
      return;
    }

    let cancelled = false;
    const loadAgreementPractices = async () => {
      setAgreementPracticesLoading(true);
      const practicesById = new Map<number, string>();
      try {
        const response = await getRxAgreementPracticeLookupService(mandatoryAgreementProductIds, true);
        const lookupList = response?.data?.data ?? [];
        lookupList.forEach((practice: { practiceId?: number; practiceName?: string }) => {
          if (practice?.practiceId == null) return;
          practicesById.set(Number(practice.practiceId), practice.practiceName || String(practice.practiceId));
        });
      } catch {
        practicesById.clear();
      } finally {
        if (!cancelled) {
          setAgreementPractices(
            Array.from(practicesById.entries()).map(([practiceId, practiceName]) => ({
              practiceId,
              practiceName,
            }))
          );
          setAgreementPracticesLoading(false);
        }
      }
    };
    void loadAgreementPractices();
    return () => {
      cancelled = true;
    };
  }, [open, requiresMandatoryAgreementPractice, selectedPharmacyId, mandatoryAgreementProductIds]);

  // A single pharmacy needs no choosing — select it so the products show straight away.
  useEffect(() => {
    if (!open) return;
    if (pharmacies.length === 1) {
      setValue("pharmacyId", String(pharmacies[0].manufacturerId));
    }
  }, [open, pharmacies, setValue]);

  useEffect(() => {
    if (!open || !showPracticeDropdown) return;
    if (practiceOptions.length === 1) {
      setValue("practiceId", String(practiceOptions[0].value));
    }
  }, [open, showPracticeDropdown, practiceOptions, setValue]);

  // CS-2075 — callers unmount the popup on product select without onClose; clear the dedupe key
  // so the next open refetches instead of reusing practiceProductsInfo another screen overwrote.
  useEffect(() => resetRxAgreementPracticeProductsFetchKey, []);

  useEffect(() => {
    if (!open) {
      resetRxAgreementPracticeProductsFetchKey();
      return;
    }
    if (!needsPracticeSelection || selectedPracticeId === "") return;

    const fetchKey = `${selectedPharmacyId}-${selectedPracticeId}`;
    if (rxAgreementPracticeProductsFetchKey === fetchKey) {
      return;
    }
    rxAgreementPracticeProductsFetchKey = fetchKey;

    const formattedDate = dayjs().format("MM/DD/YYYY");
    dispatch(
      fetchPracticeProductsDataUsingDateOfServiceAndProvider({
        practiceId: Number(selectedPracticeId),
        dateOfService: formattedDate,
        providerId: 0,
        moduleName: "",
        includeRxRequiredProducts: false,
        skipLoader: true,
      })
    );
  }, [open, dispatch, needsPracticeSelection, selectedPharmacyId, selectedPracticeId]);

  const formatExtendedPriceLabel = (value: number | string | null | undefined) => {
    if (value == null || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) {
      return priceFormatter(value);
    }
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(parsed)) {
      return priceFormatter(parsed);
    }
    return String(value);
  };

  /** Practice selected: only Rx open-access SKUs with EXECUTED agreement for that practice. */
  const mapExecutedPracticeAgreementProducts = useCallback((): RxOpenAccessProduct[] => {
    if (!practiceProductsInfo?.length || selectedPharmacyId === "") return [];
    const pharmacyId = Number(selectedPharmacyId);
    const pharmacyName = selectedPharmacy?.manufacturerName ?? null;

    const seenProductIds = new Set<number>();
    return practiceProductsInfo
      .filter((row: any) => {
        const status = String(row.agreementStatus ?? "").trim().toUpperCase();
        if (status !== EXECUTED_AGREEMENT) {
          return false;
        }
        const rowMfg = row.manufacturerId != null ? Number(row.manufacturerId) : null;
        if (rowMfg != null && rowMfg !== pharmacyId) return false;
        const productId = Number(row.productId);
        if (!rxOpenAccessProductIdsForPharmacy.has(productId) || seenProductIds.has(productId)) {
          return false;
        }
        seenProductIds.add(productId);
        return true;
      })
      .map((row: any) => {
        const openAccess = rxProductsForSelectedPharmacy.find(
          (product) => Number(product.productId) === Number(row.productId)
        );
        // const extendedPrice = row.extendedPrice ?? row.discountedPrice ?? null;
        const extendedPrice = row.extendedPrice ?? row.discountedPrice ?? singleSkuExtendedPrice(row); // CS-2075
        return {
          productId: Number(row.productId),
          productName: row.productName,
          manufacturerId: row.manufacturerId != null ? Number(row.manufacturerId) : pharmacyId,
          manufacturerName: pharmacyName,
          productCategory: row.productCategory ?? null,
          composition: row.composition ?? null,
          hcpcsCode: row.hcpcsCode ?? null,
          agreementMode: openAccess?.agreementMode ?? null,
          extendedPrice,
        };
      });
  }, [
    practiceProductsInfo,
    rxOpenAccessProductIdsForPharmacy,
    rxProductsForSelectedPharmacy,
    selectedPharmacy,
    selectedPharmacyId,
  ]);

  const filteredProducts = useMemo(() => {
    if (selectedPharmacyId === "") return [];

    let byPharmacy: RxOpenAccessProduct[];

    if (needsPracticeSelection) {
      if (selectedPracticeId === "") return [];
      byPharmacy = mapExecutedPracticeAgreementProducts();
    } else {
      byPharmacy = rxProductsForSelectedPharmacy;
    }

    if (!searchText.trim()) return byPharmacy;
    const lowerSearch = searchText.toLowerCase();
    return byPharmacy.filter(
      (product) =>
        product.productName?.toLowerCase().includes(lowerSearch) ||
        product.manufacturerName?.toLowerCase().includes(lowerSearch) ||
        product.productCategory?.toLowerCase().includes(lowerSearch)
    );
  }, [
    mapExecutedPracticeAgreementProducts,
    rxProductsForSelectedPharmacy,
    needsPracticeSelection,
    searchText,
    selectedPharmacyId,
    selectedPracticeId,
  ]);

  const isAgreementPracticeProductsLoading =
    needsPracticeSelection &&
    selectedPracticeId !== "" &&
    practiceProductsDataFetchStatus === "loading";

  const isPracticeListLoading =
    needsPracticeSelection &&
    selectedPracticeId === "" &&
    (requiresMandatoryAgreementPractice
      ? agreementPracticesLoading
      : practicesFetchStatus === "loading");

  const isLoading = needsPracticeSelection
    ? selectedPracticeId !== ""
      ? isAgreementPracticeProductsLoading ||
        rxOpenAccessProductsFetchStatus === "loading"
      : isPracticeListLoading || rxOpenAccessProductsFetchStatus === "loading"
    : rxOpenAccessProductsFetchStatus === "loading";

  const searchDisabled =
    selectedPharmacyId === "" ||
    (needsPracticeSelection && selectedPracticeId === "");

  const emptyListMessage = useMemo(() => {
    if (selectedPharmacyId === "") {
      return "Select a pharmacy to see its products.";
    }
    if (needsPracticeSelection && selectedPracticeId === "") {
      return isGenesisPharmacySelected && !requiresMandatoryAgreementPractice
        ? "Select a practice to see Genesis agreement products."
        : "Select a practice to see agreement products for this pharmacy.";
    }
    if (searchText.trim()) {
      return "No products match your search.";
    }
    if (needsPracticeSelection) {
      return isGenesisPharmacySelected && !requiresMandatoryAgreementPractice
        ? "No executed Genesis agreement products for this practice."
        : "No executed agreement products for this practice.";
    }
    return "No products available for this pharmacy.";
  }, [
    isGenesisPharmacySelected,
    needsPracticeSelection,
    requiresMandatoryAgreementPractice,
    searchText,
    selectedPharmacyId,
    selectedPracticeId,
  ]);

  useEffect(() => {
    setValue("practiceId", "");
    setSearchText("");
    setAgreementPractices([]);
    resetRxAgreementPracticeProductsFetchKey();
  }, [selectedPharmacyId, setValue]);

  const handleProductClick = (product: RxOpenAccessProduct) => {
    if (needsPracticeSelection && selectedPracticeId !== "") {
      onProductSelect(product, { practiceId: Number(selectedPracticeId) });
      return;
    }
    onProductSelect(product);
  };

  const handleClose = () => {
    setSearchText("");
    reset({ pharmacyId: "", practiceId: "" });
    resetRxAgreementPracticeProductsFetchKey();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      {/* Header */}
      {/* CS-2075 */}
      <Box sx={{ ...headerSx, flexWrap: "wrap" }}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <Pill size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>

        <Box sx={{ flex: "1 1 160px", minWidth: 0, pr: 4.5 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Select Product
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: tokens.textSecondary,
              mt: 0.15,
            }}
          >
            Choose a product to request a prescription
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={<MapIcon size={14} />}
          onClick={viewCoverage("all")}
          sx={{
            mr: 4.5,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.76rem",
            borderRadius: "8px",
            whiteSpace: "nowrap",
            color: tokens.accent,
            borderColor: `${tokens.accent}55`,
            "&:hover": { borderColor: tokens.accent, backgroundColor: `${tokens.accent}0d` },
          }}
        >
          Ship-To Coverage
        </Button>

        <IconButton onClick={handleClose} size="small" aria-label="Close" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          ...bodySx,
          minHeight: 0,
          overflowY: "hidden",
          overflowX: "hidden",
        }}
      >
        {/* CS-2075 — Pharmacy first; products are filtered to the selection */}
        <Box mb={1.8}>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 600,
              color: tokens.textSecondary,
              mb: 0.7,
              display: "block",
            }}
          >
            Select Pharmacy{" "}
            <Typography component="span" sx={{ color: tokens.accent, fontSize: "18px" }}>
              *
            </Typography>
          </Typography>
          <Box sx={dropdownWrapSx()}>
            <CustomDropdownField
              name="pharmacyId"
              control={control}
              options={pharmacyOptions}
              size="small"
              placeholderText={
                pharmacyOptions.length === 0 ? "No pharmacy found" : "Choose a pharmacy..."
              }
              sortOptions={false}
            />
          </Box>
        </Box>

        {showPracticeDropdown && (
          <Box mb={1.8}>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: tokens.textSecondary,
                mb: 0.7,
                display: "block",
              }}
            >
              Select Practice{" "}
              <Typography component="span" sx={{ color: tokens.accent, fontSize: "18px" }}>
                *
              </Typography>
            </Typography>
            <Box sx={dropdownWrapSx()}>
              <CustomDropdownField
                name="practiceId"
                control={control}
                options={practiceOptions}
                size="small"
                placeholderText={
                  agreementPracticesLoading
                    ? "Loading practices..."
                    : practiceOptions.length === 0
                      ? "No practice with executed product agreement"
                      : "Choose a practice..."
                }
                sortOptions={false}
              />
            </Box>
          </Box>
        )}

        {/* Search Field */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search products..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          disabled={searchDisabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: tokens.textSecondary, fontSize: "1.2rem" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: tokens.inputBg,
              borderRadius: "10px",
              fontSize: "0.88rem",
              "& fieldset": { borderColor: tokens.inputBorder },
              "&:hover fieldset": { borderColor: tokens.accentHover },
              "&.Mui-focused fieldset": {
                borderColor: tokens.accent,
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input": {
              color: tokens.textPrimary,
              fontSize: "0.88rem",
            },
          }}
        />

        {/* Product List */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={32} sx={{ color: tokens.accent }} />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <Typography
              sx={{ fontSize: "0.88rem", color: tokens.textSecondary }}
            >
              {emptyListMessage}
            </Typography>
          </Box>
        ) : (
          // CS-2075
          <Box sx={{ ...scrollAreaSx({ maxHeight: "320px" }), scrollbarGutter: "auto", pr: 0, mr: 0 }}>
            {filteredProducts.map((product) => {
              const extendedPriceLabel = formatExtendedPriceLabel(product.extendedPrice);
              return (
              <Box
                key={product.productId}
                onClick={() => handleProductClick(product)}
                sx={{
                  p: 1.8,
                  borderRadius: "10px",
                  border: `1px solid ${tokens.border}`,
                  backgroundColor: tokens.inputBg,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                  "&:hover": {
                    borderColor: tokens.accent,
                    backgroundColor: `${tokens.accent}0d`,
                    boxShadow: `0 0 0 3px ${tokens.accent}14`,
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: tokens.textPrimary,
                    lineHeight: 1.3,
                    overflowWrap: "anywhere", // CS-2075
                  }}
                >
                  {product.productName}
                </Typography>
                {product.manufacturerName && (
                  <Typography
                    sx={{
                      fontSize: "0.76rem",
                      color: tokens.textSecondary,
                      mt: 0.3,
                    }}
                  >
                    {product.manufacturerName}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.8, mt: 0.6 }}>
                  {product.productCategory && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.2,
                        borderRadius: "6px",
                        backgroundColor: `${tokens.accent}1a`,
                        border: `1px solid ${tokens.accent}33`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          color: tokens.accent,
                          letterSpacing: "0.03em",
                        }}
                      >
                        {product.productCategory}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    onClick={viewCoverage(resolvePharmacyId(product.manufacturerName, product.manufacturerId), true)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.2,
                      borderRadius: "999px",
                      border: `1px solid ${tokens.accent}40`,
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: `${tokens.accent}14`,
                        borderColor: tokens.accent,
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <MapIcon size={12} color={tokens.accent} />
                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: tokens.accent, letterSpacing: "0.02em" }}>
                      Ship-To Coverage
                    </Typography>
                  </Box>
                </Box>
                </Box>
                {extendedPriceLabel && (
                  <Box
                    sx={{
                      textAlign: "right",
                      flexShrink: 0,
                      minWidth: 108,
                      pl: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        color: tokens.textSecondary,
                        letterSpacing: "0.07em",
                        lineHeight: 1.2,
                      }}
                    >
                      PRICE
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: tokens.textPrimary,
                        whiteSpace: "nowrap",
                        mt: 0.35,
                        lineHeight: 1.2,
                      }}
                    >
                      {extendedPriceLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={footerSx}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
            onClick={handleClose}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RxProductSelectionPopup;
